/* eslint-disable camelcase */

import { getEnvironment } from '../../../shared/utils';

export const genericErrorMessage = `Please retry and if this persists, send an email to our support team - support@formpl.us - to fix this`;

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))}${sizes[i]}`;
};

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatDate = {
  informal: (value) => {
    const today = new Date();
    const date = new Date(value || today); //  Hide NaN and undefined display when date is loading by using today's date
    const dayWithSuffix = (day) => {
      if (day[-1] === 1) return `${day}ist`;
      if (day[-1] === 2) return `${day}nd`;
      if (day[-1] === 3) return `${day}rd`;
      return `${day}th`;
    };
    return `${dayWithSuffix(date.getDate())} ${months[date.getMonth()]}, ${date.getFullYear()}`;
  },
  ISO: (date) => {
    const parsedDate = new Date(date);
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const hour = String(parsedDate.getHours() % 12 || 12).padStart(2, '0');
    const minute = String(parsedDate.getMinutes()).padStart(2, '0');
    const ampm = parsedDate.getHours() >= 12 ? 'PM' : 'AM';

    const formattedDate = `${year}-${month}-${day}, ${hour}:${minute} ${ampm}`;
    return formattedDate;
  },
};

export const formatQuota = (quota) => (quota === 'inf' ? Infinity : parseInt(quota, 10));

// Intercom events tracking
export const trackSettings = (setting, data) => {
  const action = `account_settings-${setting}`;
  const metadata = { setting };
  // You can only add a maximum of 3 properties in data because Intercom allows 5 metadata max
  Object.entries(data).forEach(([key, value]) => {
    if (value) metadata[key] = value;
  });
  window.Intercom('trackEvent', action, metadata);
};

// Development keys here are strictly for MY testing purposes alone.
// No form responses are actually submitted during any of these
// actions(account deletion & subscription cancellation) on localhost
export const getAccountActionFormId = (formType) => {
  const formIds = {
    cancel_subscription: {
      development: '5770237022568448',
      production: '5738880681771008',
      staging: '5191433130606592',
    },
    delete_account: {
      development: '6610813662003200',
      production: '6051371378933760',
      staging: '5088625974837248',
    },
  };

  const environment = getEnvironment();
  return formIds[formType][environment];
};

export const accountActionFormData = [
  {
    id: getAccountActionFormId('cancel_subscription'),
    type: 'cancel_subscription',
  },
  {
    id: getAccountActionFormId('delete_account'),
    type: 'delete_account',
  },
];

// Development keys here are strictly for MY testing purposes alone.
// No form responses are actually submitted during any of these
// actions(account deletion & subscription cancellation) on localhost
export const getActionFeedbackFormControlInternalId = ({ formType, controlKey }) => {
  const formControlInternalIds = {
    cancel_subscription: [
      {
        key: 'first_name',
        internal_ids: {
          development: '1696336230666',
          production: '1681382364471',
          staging: '1681835930810',
        },
      },
      {
        key: 'last_name',
        internal_ids: {
          development: '1696336279071',
          production: '1681382365828',
          staging: '1681835949092',
        },
      },
      {
        key: 'email',
        internal_ids: {
          development: '1696336155287',
          production: '1681382361280',
          staging: '1681835922812',
        },
      },
      {
        key: 'reason_for_cancellation',
        internal_ids: {
          development: '1696336318769',
          production: '1681382461599',
          staging: '1681835964624',
        },
      },
      {
        key: 'what_are_your_present_needs',
        internal_ids: {
          development: '1696336454343',
          production: '1681382689391',
          staging: '1681836090322',
        },
      },
      {
        key: 'how_can_we_meet_your_needs_more_accurately',
        internal_ids: {
          development: '1696336487134',
          production: '1681382736865',
          staging: '1681836097698',
        },
      },
      {
        key: 'what_are_the_features_you_need',
        internal_ids: {
          development: '1696336522332',
          production: '1681382827648',
          staging: '1681836121229',
        },
      },
      {
        key: 'suggestions_for_better_experience',
        internal_ids: {
          development: '1696336560137',
          production: '1681383670813',
          staging: '1681836136795',
        },
      },
      {
        key: 'other_reason',
        internal_ids: {
          development: '1696336620413',
          production: '1681382869382',
          staging: '1682590092118',
        },
      },
      {
        key: 'final_decision',
        internal_ids: {
          development: '1681476407377',
          production: '1681382950817',
          staging: '1681836159378',
        },
      },
    ],
    delete_account: [
      {
        key: 'first_name',
        internal_ids: {
          development: '1681476019242',
          production: '1681382364471',
          staging: '1681835930810',
        },
      },
      {
        key: 'last_name',
        internal_ids: {
          development: '1681476019982',
          production: '1681382365828',
          staging: '1681835949092',
        },
      },
      {
        key: 'email',
        internal_ids: {
          development: '1681476006428',
          production: '1681382361280',
          staging: '1681835922812',
        },
      },
      {
        key: 'reason_for_deletion',
        internal_ids: {
          development: '1681476108453',
          production: '1681382461599',
          staging: '1681835964624',
        },
      },
      {
        key: 'what_are_your_present_needs',
        internal_ids: {
          development: '1681476168188',
          production: '1681382689391',
          staging: '1681836090322',
        },
      },
      {
        key: 'how_can_we_meet_your_needs_more_accurately',
        internal_ids: {
          development: '1681476175902',
          production: '1681382736865',
          staging: '1681836097698',
        },
      },
      {
        key: 'what_are_the_features_you_need',
        internal_ids: {
          development: '1681476196024',
          production: '1681382827648',
          staging: '1681836121229',
        },
      },
      {
        key: 'other_reason',
        internal_ids: {
          development: '1681476349300',
          production: '1681382869382',
          staging: '1681836136795',
        },
      },
      {
        key: 'final_decision',
        internal_ids: {
          development: '1681476407377',
          production: '1681382950817',
          staging: '1681836159378',
        },
      },
    ],
  };

  const environment = getEnvironment();
  const control = formControlInternalIds[formType].find(({ key }) => key === controlKey);

  return control?.internal_ids[environment] || '';
};

export const extractSecretFromQRCodeUrl = (qrCodeUrl) => {
  try {
    const url = new URL(qrCodeUrl);
    return url.searchParams.get('secret');
  } catch (error) {
    console.error('Error parsing URL:', error);
    return null;
  }
};